import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

import StepIndicator from "@/components/ai-test-generator/StepIndicator";
import PipelineInputForm from "@/components/ai-test-generator/PipelineInputForm";
import PipelineProgress from "@/components/ai-test-generator/PipelineProgress";
import TestReviewPane from "@/components/ai-test-generator/TestReviewPane";

const FOCUS_MAP = {
  psychometric: "Psychometric Assessment",
  emotional_intelligence: "Emotional Intelligence",
  scenario_based: "High-Pressure Scenarios",
  technical: "Technical Skills",
};

export default function AITestGeneratorPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    role_title: "",
    job_description: "",
    seniority_level: "mid",
    focus_areas: [],
    test_types: [],
    target_question_count: 25,
    time_limit_minutes: 30,
    passing_score: 70,
    test_type: "technical",
  });

  const [focusAreasInput, setFocusAreasInput] = useState("");
  const [runId, setRunId] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedTest, setGeneratedTest] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFocusAreasChange = (value) => {
    setFocusAreasInput(value);
    const areas = value.split(",").map((a) => a.trim()).filter((a) => a.length > 0);
    setFormData((prev) => ({ ...prev, focus_areas: areas }));
  };

  const pollPipelineStatus = async (id, retryCount = 0) => {
    const maxRetries = 3;
    const pollInterval = 2000;
    try {
      const response = await api.get(`/admin/tests/ai-generate/status/${id}`);
      const status = response.data;
      setPipelineStatus(status);

      if (status.status === "completed" && status.final_test_draft) {
        setGeneratedTest(status.final_test_draft);
        setIsGenerating(false);
        setStep(3);
        toast.success("Test generated successfully!");
      } else if (status.status === "failed") {
        toast.error(`Generation failed: ${status.error_message || "Unknown error"}`);
        setIsGenerating(false);
        setStep(1);
      } else {
        setTimeout(() => pollPipelineStatus(id, 0), pollInterval);
      }
    } catch (error) {
      if (error.response?.status === 404 && retryCount < maxRetries) {
        setTimeout(() => pollPipelineStatus(id, retryCount + 1), pollInterval);
        return;
      }
      console.error("Polling error:", error);
      if (retryCount >= maxRetries) {
        toast.error("Error checking generation status. Please refresh and try again.");
        setIsGenerating(false);
        setStep(1);
      } else {
        setTimeout(() => pollPipelineStatus(id, retryCount + 1), pollInterval);
      }
    }
  };

  const startGeneration = async () => {
    if (!formData.role_title || !formData.job_description) {
      toast.error("Please fill in role title and job description");
      return;
    }
    if (formData.test_types && formData.test_types.length === 0) {
      toast.error("Please select at least one test type");
      return;
    }

    setIsGenerating(true);
    setStep(2);

    try {
      const { test_types, ...pipelineData } = formData;
      const enhancedFocusAreas = [
        ...pipelineData.focus_areas,
        ...test_types.map((type) => FOCUS_MAP[type] || type),
      ];

      const response = await api.post("/admin/tests/ai-generate/start", {
        input_data: { ...pipelineData, focus_areas: enhancedFocusAreas },
      });
      setRunId(response.data.run_id);
      pollPipelineStatus(response.data.run_id);
    } catch (error) {
      console.error("AI Generation Error:", error);

      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        setTimeout(() => {
          window.location.href = "/admin/login";
        }, 2000);
        setIsGenerating(false);
        setStep(1);
        return;
      }

      const detail = error.response?.data?.detail;
      let errorMessage = "Failed to start AI generation";
      if (Array.isArray(detail)) {
        errorMessage = detail.map((err) => `${err.loc?.join(".") || "field"}: ${err.msg}`).join("; ");
      } else if (typeof detail === "string") {
        errorMessage = detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      setIsGenerating(false);
      setStep(1);
    }
  };

  const finalizeTest = async () => {
    try {
      await api.post(`/admin/tests/ai-generate/finalize/${runId}`);
      toast.success("Test saved successfully!");
      navigate(`/admin/tests`);
    } catch (error) {
      toast.error("Failed to save test");
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="ai-test-generator-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Test Generator</h1>
              <p className="text-gray-600">5-agent pipeline for intelligent assessment creation</p>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-6">
            <StepIndicator number={1} label="Configure" active={step === 1} completed={step > 1} />
            <div className="flex-1 h-px bg-gray-200"></div>
            <StepIndicator number={2} label="Generate" active={step === 2} completed={step > 2} />
            <div className="flex-1 h-px bg-gray-200"></div>
            <StepIndicator number={3} label="Review" active={step === 3} completed={false} />
          </div>
        </div>

        {step === 1 && (
          <PipelineInputForm
            formData={formData}
            focusAreasInput={focusAreasInput}
            onFieldChange={handleInputChange}
            onFocusAreasChange={handleFocusAreasChange}
            onSubmit={startGeneration}
          />
        )}

        {step === 2 && <PipelineProgress pipelineStatus={pipelineStatus} inputSummary={formData} />}

        {step === 3 && generatedTest && (
          <TestReviewPane test={generatedTest} onStartOver={() => setStep(1)} onFinalize={finalizeTest} />
        )}
      </div>
    </div>
  );
}
